import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ItemAuctionService} from '../shared/item-auction.service';
import {DealerDismantlerReasonModel} from '../shared/dealer-dismantler-reason.model';
import {ItemAuctionInfo} from '../shared/item-auction-info.model';
import {ButtonRadioOption, DialogService} from '@rbaip/ui-component';
import {map, startWith} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {FormControl} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'lib-item-listing-selling-method',
  templateUrl: './item-selling-method.component.html',
  styleUrls: ['./item-selling-method.component.scss']
})
export class ItemSellingMethodComponent implements OnInit, OnChanges {

  @Input() itemAuctionInfo: ItemAuctionInfo;
  @Input() writable: boolean;
  @Output() refreshAuctionInfoEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  filteredDismantlerReasons: Observable<DealerDismantlerReasonModel[]>;
  dealerDismantlerReasonList: Array<DealerDismantlerReasonModel> = [];
  dealerDismantlerReasonControl: FormControl = new FormControl();
  yesNoRadioButtonOptions: ButtonRadioOption[] = [{display: 'No', value: 'No'}, {display: 'Yes', value: 'Yes'}];

  constructor(private itemAuctionService: ItemAuctionService,
              private dialogService: DialogService) {
  }

  ngOnInit(): void {
    this.setDealerDismantlerReasonFromItemAuctionInfo();
    this.getDealerDismantlerReasons();
    this.dealerDismantlerReasonControl.valueChanges
      .pipe(
        startWith(''),
        map(searchKey => {
          return searchKey ? this.filterDismantlerInfo(searchKey) : this.dealerDismantlerReasonList.slice();
        })
      ).subscribe(matchedOptions => {
        this.filteredDismantlerReasons = of(matchedOptions);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemAuctionInfo && changes.itemAuctionInfo.currentValue) {
      this.itemAuctionInfo = this.itemAuctionInfo.clone();
      this.setDealerDismantlerReasonFromItemAuctionInfo();
    }
  }

  private filterDismantlerInfo(desc: string): DealerDismantlerReasonModel[] {
    if (desc !== '') {
      const filterValue = desc.toLowerCase();
      return this.dealerDismantlerReasonList.filter(reasonOption => reasonOption.description.toLowerCase().indexOf(filterValue) === 0);
    }
  }

  public onDealerDismantlerReasonSelected(event: MatAutocompleteSelectedEvent) {
    let isValidInput = false;
    if (event.option) {
      const isOtherOption = event.option.value === 'Other';
      if (isOtherOption) {
        this.itemAuctionInfo.dealerDismantlerReasonId = null;
        this.itemAuctionInfo.dealerDismantlerReason = null;
      } else {
        this.itemAuctionInfo.dealerDismantlerReason = event.option.value;
        this.itemAuctionInfo.dealerDismantlerReasonId = this.dealerDismantlerReasonList
          .find(reason => reason.description === this.itemAuctionInfo.dealerDismantlerReason).id;
        isValidInput = true;
      }
    } else {
      isValidInput = true;
      this.itemAuctionInfo.dealerDismantlerReasonId = null;
      this.itemAuctionInfo.dealerDismantlerReason = null;
    }
    this.repopulateTheFullListOfDealerDismantlerReasonsAfterSelection();
    if (isValidInput) {
      this.updateAuctionSellingMethodInfo();
    }
  }

  private setDealerDismantlerReasonFromItemAuctionInfo(): void {
    this.dealerDismantlerReasonControl.setValue(this.itemAuctionInfo.dealerDismantlerReason);
    setTimeout(() => this.repopulateTheFullListOfDealerDismantlerReasonsAfterSelection(), 500);
  }

  private repopulateTheFullListOfDealerDismantlerReasonsAfterSelection(): void {
    this.filteredDismantlerReasons = of(this.dealerDismantlerReasonList.slice());
  }

  public updateAuctionSellingMethodInfo(reloadDealerDismantlerReasons?: boolean) {
    this.itemAuctionInfo.translateToBoolean();
    this.focusDropdownIcon();
    this.itemAuctionService.updateAuctionSellingMethodInfo(this.itemAuctionInfo)
      .subscribe(assetListingResponse => {
        if (!assetListingResponse) {
          this.dialogService.showMessage('Successfully updated Selling Method info!');
        }
        this.refreshAuctionInfo();
        if (reloadDealerDismantlerReasons) {
          this.getDealerDismantlerReasons();
        }
      });
  }

  private focusDropdownIcon(): void {
    const reasonDropdownBtnElem: HTMLElement = document.getElementById('reason-dropdown-btn');
    if (reasonDropdownBtnElem) {
      reasonDropdownBtnElem.focus();
    }
  }

  public toggleDealerDismantlerReason(): void {
    if (this.itemAuctionInfo.dealerDismantlerEnabled === 'No') {
      this.itemAuctionInfo.dealerDismantlerReasonId = null;
      this.itemAuctionInfo.dealerDismantlerReason = null;
      this.dealerDismantlerReasonControl.setValue('');
      this.updateAuctionSellingMethodInfo();
    }
  }

  private refreshAuctionInfo(): void {
    this.refreshAuctionInfoEvent.emit(true);
  }

  public getDealerDismantlerReasons() {
    this.dealerDismantlerReasonList = [];
    this.itemAuctionService.findDismantlerReasonList()
      .subscribe(response => {
        if (response && !response.error) {
          const dealerDismantlerReasonsJson = response.data;
          for (const dealerDismantlerReasonJson of dealerDismantlerReasonsJson) {
            const dealerDismantlerReason = Object.assign(new DealerDismantlerReasonModel(), dealerDismantlerReasonJson);
            this.dealerDismantlerReasonList.push(dealerDismantlerReason);
          }
          this.repopulateTheFullListOfDealerDismantlerReasonsAfterSelection();
        } else {
          this.dialogService.showMessage('Failed to load Dealer Dismantler Reasons!');
        }
      });
  }

  public addDealerDismantlerReason() {
    this.itemAuctionService.addOtherReason(this.itemAuctionInfo.dealerDismantlerReason)
      .subscribe(response => {
        if (!response) {
          this.dialogService.showMessage('Successfully saved a Dealer Dismantler Reason!');
          this.dealerDismantlerReasonList = [];
          this.getDealerDismantlerReasons();
        } else {
          this.dialogService.showMessage('Failed to save a new Dealer Dismantler Reason!');
        }
      });
  }

  onOtherDealerDismantlerReasonChanged() {
    if (this.itemAuctionInfo.dealerDismantlerReason) {
      this.updateAuctionSellingMethodInfo(true);
    }
  }
}
